/* PTO Buffer Planner. Works out what each working week owes your weeks off. */
import React, { useMemo, useState } from 'react';
import { PitCrewToolShell, ToolLabel, ToolInput, ToolApplyBar, COLORS } from './PitCrewToolShell';

const { INK } = COLORS;

const PtoBufferModal = ({ onApply, onClose, formatCurrency, suggestedWeeklyPay = 0 }) => {
  const [weeksOff, setWeeksOff] = useState(4);
  const [weeklyPay, setWeeklyPay] = useState(suggestedWeeklyPay ? String(Math.round(suggestedWeeklyPay)) : '');

  const plan = useMemo(() => {
    const pay = parseFloat(weeklyPay) || 0;
    const workingWeeks = 52 - weeksOff;
    if (pay <= 0 || workingWeeks <= 0) return null;
    const total = pay * weeksOff;
    return { total, workingWeeks, perWorkingWeek: total / workingWeeks, pay };
  }, [weeklyPay, weeksOff]);

  return (
    <PitCrewToolShell
      eyebrow="Pit Crew tool"
      title="PTO Buffer Planner"
      lead="Time off only feels like time off when it is already paid for. This works out what each working week owes your weeks away. Nothing here is saved."
      onClose={onClose}
      testId="pto-buffer"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <ToolLabel>Weeks off you want</ToolLabel>
          <div className="flex flex-wrap gap-2">
            {[2, 4, 6, 8, 12].map((w) => (
              <button
                key={w}
                onClick={() => setWeeksOff(w)}
                className="px-4 py-2.5 font-body text-sm transition-colors"
                style={{
                  background: weeksOff === w ? INK : '#FFFFFF',
                  color: weeksOff === w ? '#F2EFE7' : INK,
                  border: `1px solid ${weeksOff === w ? INK : '#E0D9CB'}`,
                  borderRadius: 0,
                }}
                data-testid={`pto-weeks-${w}`}
              >
                {w} weeks
              </button>
            ))}
          </div>
        </div>
        <div>
          <ToolLabel>What does a paid week need to cover?</ToolLabel>
          <ToolInput type="number" prefix="$" value={weeklyPay} onChange={(e) => setWeeklyPay(e.target.value)} placeholder="1,200" data-testid="pto-weekly-pay-input" />
          <p className="font-body text-xs mt-1.5" style={{ color: '#8B7E6A' }}>
            Usually your weekly take-home, what a normal week puts in your pocket.
          </p>
        </div>
      </div>

      {plan && (
        <div className="p-6" style={{ background: '#FFFFFF', border: '1px solid #E8E2D5' }} data-testid="pto-buffer-result">
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Set aside each working week</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(plan.perWorkingWeek)}</p>
            </div>
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Your buffer by year end</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(plan.total)}</p>
            </div>
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Working weeks</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{plan.workingWeeks}</p>
            </div>
          </div>
          <p className="font-body text-sm leading-relaxed mt-5 pt-4 border-t" style={{ color: '#5D5A6B', borderColor: '#E8E2D5' }}>
            {weeksOff} weeks off at {formatCurrency(plan.pay)} a week, funded by the {plan.workingWeeks} weeks
            you do work. Your calculator builds this into your target, so the time off pays itself.
          </p>
        </div>
      )}

      <ToolApplyBar
        testId="pto-buffer"
        applyLabel={`Use ${weeksOff} weeks off`}
        note="This fills in your weeks off step, you can still change it."
        onApply={() => onApply({ weeksOff })}
        onClose={onClose}
      />
    </PitCrewToolShell>
  );
};

export default PtoBufferModal;
